<script>
  import { createEventDispatcher } from "svelte";
  import { ICONS } from "../../../../../js/icons";
  import PlayerManagement from "../PlayerManagement.svelte";
  import ProgressBanner from "../ProgressBanner.svelte";
  import { getPragmaticDemoPlayers } from "../../../../config/demoClient.js";

  export let campaign = {};
  export let clientId = "";
  export let providerId = 67;
  export let excludeIds = [];
  export let busy = false;
  export let progress = null;

  const dispatch = createEventDispatcher();
  let validationError = "";
  let showPlayerManagement = false;
  let showAssignmentForm = false;
  let assignmentAmount = "";
  let pendingPlayers = [];

  const clean = (value) => String(value ?? "").trim();

  function campaignBrand() {
    const brand = campaign?.brand;
    if (brand && typeof brand === "object") {
      return clean(brand.code ?? brand.value ?? brand.name);
    }
    return clean(brand);
  }

  function monetaryAmount(value) {
    if (clean(value) === "") return null;
    const amount = Number(value);
    return Number.isFinite(amount) ? amount : null;
  }

  function openPlayerSelector() {
    validationError = "";
    const mode = clean(campaign?.assignationMode).toUpperCase();
    if (!["AUTO", "MANUAL"].includes(mode)) {
      validationError = "Configura el tipo de asignación antes de añadir jugadores.";
      return;
    }
    showPlayerManagement = true;
  }

  function handlePlayersSelected(event) {
    validationError = "";
    const players = event.detail?.players || [];
    showPlayerManagement = false;
    if (!players.length) {
      validationError = "Selecciona al menos un jugador.";
      return;
    }
    pendingPlayers = players;
    assignmentAmount = "";
    if (isCashback) {
      submitPlayers(players);
      return;
    }
    showAssignmentForm = true;
  }

  function returnToPlayerSelector() {
    validationError = "";
    showPlayerManagement = true;
  }

  function submitPlayers(players = pendingPlayers) {
    validationError = "";
    const brandId = campaignBrand();
    const providerOfferId = clean(campaign?.providerCampaignId);
    const hasAmount = clean(assignmentAmount) !== "";
    const amount = monetaryAmount(assignmentAmount);

    if (!players.length) {
      validationError = "Selecciona al menos un jugador.";
      return;
    }
    if (!brandId || !providerOfferId) {
      validationError = "La campaña no contiene brand o providerOfferId.";
      return;
    }
    if (requiresBonusAmount && (!hasAmount || amount === null || amount <= 0)) {
      validationError = "Ingresa un monto de bono mayor que cero.";
      return;
    }
    if (!isCashback && amount !== null && hasMaximumBonusAmount && amount > Number(maximumBonusAmount)) {
      validationError = `El monto del bono no puede superar ${campaign?.currency || ""} ${maximumBonusAmount}.`;
      return;
    }
    const invalidPlayers = players.filter(
      (player) => !clean(player?.currency) || !clean(player?.country),
    );
    if (invalidPlayers.length) {
      validationError = `${invalidPlayers.length} jugador(es) no tienen moneda o país configurado.`;
      return;
    }

    showAssignmentForm = false;
    pendingPlayers = [];
    dispatch("submit", {
      assignments: players.map((player) => ({
        player,
        payload: {
          loginName: clean(player?.username_origin || player?.username || player?.label) || null,
          brandId,
          playerId: String(player?.id),
          providerId,
          currency: clean(player?.currency).toUpperCase(),
          country: clean(player?.country).toUpperCase(),
          providerBonusCode: clean(campaign?.campaignId) || null,
          providerOfferId,
          ...(!isCashback && amount !== null ? { bonusAmount: amount } : {}),
          bonusId: null,
        },
      })),
    });
  }

  function cancelPlayerBonus(player, allBonuses) {
    const playerId = clean(player?.playerId);
    const providerBonusId = clean(player?.baseBonusCode);
    if (!playerId || (!allBonuses && !providerBonusId)) return;

    const message = allBonuses
      ? `¿Cancelar todos los bonos de ${player?.playerName || playerId}?`
      : `¿Cancelar el bono de esta campaña para ${player?.playerName || playerId}?`;
    if (!confirm(message)) return;

    dispatch("cancelbonus", {
      playerId,
      providerId,
      campaignId: clean(campaign?.campaignId),
      allBonuses,
      providerBonusId,
    });
  }

  $: assignedPlayers = Array.isArray(campaign?.playerList)
    ? campaign.playerList.filter((player) => Number(player?.playerStatus) !== 0)
    : [];
  $: assignmentMode = clean(campaign?.assignationMode).toUpperCase();
  $: bonusType = clean(campaign?.providerCampaignType ?? campaign?.bonusType).toUpperCase();
  $: isCashback = bonusType === "CASHBACK";
  $: requiresBonusAmount = ["FREE_BET", "RISK_FREE_BET"].includes(bonusType);
  $: configuredAmount = campaign?.sportsConfig?.bonusAmount;
  $: maximumBonusAmount = campaign?.sportsConfig?.maximumBonusAmount;
  $: hasMaximumBonusAmount =
    maximumBonusAmount !== null &&
    maximumBonusAmount !== undefined &&
    maximumBonusAmount !== "" &&
    Number.isFinite(Number(maximumBonusAmount)) &&
    Number(maximumBonusAmount) > 0;
  $: demoPlayers = getPragmaticDemoPlayers(
    campaign?.client ?? campaign?.clientCode ?? "",
    clientId,
    campaign?.currency ?? "",
  );
</script>

<div class="modal-backdrop fade show" aria-hidden="true"></div>
<div class="modal d-block" tabindex="-1" style="background:rgba(0,0,0,.3);">
  <div class="modal-dialog modal-lg modal-dialog-centered fixed-modal">
    <div class="modal-content modal-fixed">
      <div class="modal-header">
        <h5 class="modal-title">{campaign?.campaignName || "Campaña"}</h5>
        <button type="button" class="btn-close" aria-label="Cerrar" disabled={busy} on:click={() => dispatch("close")}></button>
      </div>

      <div class="modal-body modal-body-fixed">
        <div class="top-actions d-flex justify-content-between align-items-center mb-2">
          <div class="small text-muted">
            Cliente: {campaign?.client?.clientName || "—"} ·
            Moneda: {campaign?.currency || "—"} ·
            Asignación: {assignmentMode || "—"}
            {#if assignmentMode === "AUTO"}
              · Monto automático: {configuredAmount ?? "—"}
            {/if}
          </div>
          {#if !showAssignmentForm}
            <button type="button" class="btn btn-primary btn-sm" disabled={busy || !clientId} on:click={openPlayerSelector}>
              {@html ICONS.addCircle} Agregar jugadores
            </button>
          {:else}
            <span class="step-indicator">Paso 2 de 2</span>
          {/if}
        </div>

        <div class="body-scroll">
          {#if progress}
            <ProgressBanner
              sticky={false}
              stage={progress.done < progress.total
                ? "adding"
                : progress.errors >= progress.total
                  ? "error"
                  : progress.errors > 0
                    ? "warning"
                    : "done"}
              total={progress.total}
              done={progress.done}
              errors={progress.errors || 0}
            />
          {/if}
          {#if validationError}<div class="alert alert-danger" role="alert">{validationError}</div>{/if}

          {#if showAssignmentForm}
            <section class="assignment-panel">
              <div class="assignment-step-header">
                <button
                  type="button"
                  class="back-button"
                  title="Volver a seleccionar jugadores"
                  aria-label="Volver a seleccionar jugadores"
                  disabled={busy}
                  on:click={returnToPlayerSelector}
                >
                  {@html ICONS.left}
                </button>
                <h6>Datos de asignación</h6>
              </div>
              <p class="selected-summary">
                Jugadores seleccionados: <strong>{pendingPlayers.length}</strong>
              </p>
              <div class="assignment-field">
                <label for="manual-sport-bonus-amount">
                  Monto del bono
                  {#if requiresBonusAmount}<span class="required">*</span>{:else}<span class="optional">(opcional)</span>{/if}
                </label>
                <div class="input-group">
                  <span class="input-group-text">{campaign?.currency || "—"}</span>
                  <input
                    id="manual-sport-bonus-amount"
                    class="form-control"
                    type="number"
                    min="0.01"
                    max={hasMaximumBonusAmount ? maximumBonusAmount : undefined}
                    step="0.01"
                    placeholder="0.00"
                    bind:value={assignmentAmount}
                    disabled={busy}
                  />
                </div>
                {#if hasMaximumBonusAmount}
                  <div class="maximum-amount-help">
                    <small>El máximo asignado para el bonus es {campaign?.currency || ""} {maximumBonusAmount}.</small>
                    <button
                      type="button"
                      class="use-maximum-button"
                      disabled={busy}
                      on:click={() => (assignmentAmount = maximumBonusAmount)}
                    >
                      Usar monto máximo
                    </button>
                  </div>
                {/if}
              </div>
            </section>
          {/if}

          {#if !showAssignmentForm}
            {#if assignedPlayers.length === 0}
              <div class="empty">No hay jugadores en esta campaña aún.</div>
            {:else}
              <div class="table-responsive assigned-table-wrapper">
                <table class="table table-striped table-hover table-bordered align-middle">
                  <thead>
                    <tr><th>#</th><th>Player ID</th><th>Jugador</th><th>Estado</th><th>Acciones</th></tr>
                  </thead>
                  <tbody>
                    {#each assignedPlayers as player, index (`${player?.playerId || "player"}-${index}`)}
                      <tr>
                        <td>{index + 1}</td>
                        <td>{player?.playerId || "—"}</td>
                        <td>{player?.playerName || "—"}</td>
                        <td>
                          <span class="player-status" class:active-status={Number(player?.playerStatus) === 1} class:inactive-status={Number(player?.playerStatus) !== 1}>
                            {Number(player?.playerStatus) === 1 ? "Activo" : "Inactivo"}
                          </span>
                        </td>
                        <td>
                          <div class="d-flex gap-1" role="group" aria-label="acciones">
                            <button type="button" class="btn btn-icon btn-delete" title="Eliminar" aria-label="Eliminar" disabled={busy || Number(player?.playerStatus) !== 1 || !player?.baseBonusCode} on:click={() => cancelPlayerBonus(player, false)}>
                              {@html ICONS.delete}
                            </button>
                            <button type="button" class="btn btn-delete btn-delete-all" title="Eliminar todos los bonos" aria-label="Eliminar todos los bonos" disabled={busy} on:click={() => cancelPlayerBonus(player, true)}>
                              {@html ICONS.delete}<span>Todos</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    {/each}
                  </tbody>
                </table>
              </div>
            {/if}
          {/if}
        </div>
      </div>

      <div class="modal-footer sticky-footer">
        {#if showAssignmentForm}
          <button type="button" class="btn btn-secondary" disabled={busy} on:click={() => { showAssignmentForm = false; pendingPlayers = []; assignmentAmount = ""; validationError = ""; }}>Cancelar</button>
          <button type="button" class="btn btn-primary" disabled={busy} on:click={() => submitPlayers()}>{busy ? "Asignando…" : "Asignar bono"}</button>
        {:else}
          <button type="button" class="btn btn-secondary" disabled={busy} on:click={() => dispatch("close")}>Cerrar</button>
        {/if}
      </div>
    </div>
  </div>
</div>

{#if showPlayerManagement}
  <div class="nested-dim" aria-hidden="true"></div>
  <PlayerManagement
    {clientId}
    currency={campaign?.currency || ""}
    initialSelected={pendingPlayers.map((player) => String(player?.id)).filter(Boolean)}
    idStrategy="auto"
    {excludeIds}
    staticPlayers={demoPlayers}
    confirmLabel={isCashback ? "Asignar bono" : "Continuar"}
    stepLabel={isCashback ? "" : "Paso 1 de 2"}
    on:close={() => (showPlayerManagement = false)}
    on:confirm={handlePlayersSelected}
  />
{/if}

<style>
  .fixed-modal { max-width: 900px; }
  .modal-fixed { height: 80vh; display: flex; flex-direction: column; }
  .modal-body-fixed { flex: 1 1 auto; min-height: 0; display: flex; flex-direction: column; overflow: hidden; }
  .body-scroll { position: relative; flex: 1 1 auto; min-height: 0; overflow: auto; }
  .modal-footer.sticky-footer { flex: 0 0 auto; position: sticky; bottom: 0; z-index: 1; margin-top: 0; border-top: 1px solid var(--bs-border-color, #e5e7eb); background: #fff; }
  .top-actions { flex: 0 0 auto; gap: 1rem; }
  .nested-dim { position: fixed; inset: 0; z-index: 1059; background: rgba(0, 0, 0, 0.45); }
  .assignment-panel { margin-bottom: 1rem; padding: 0.85rem; border: 1px solid #e5e7eb; border-radius: 0.5rem; background: #fafbfc; }
  .assignment-step-header { display: flex; align-items: center; gap: 0.65rem; margin-bottom: 0.75rem; }
  .assignment-step-header h6 { margin: 0; font-weight: 700; }
  .step-indicator { display: inline-flex; align-items: center; padding: 0.25rem 0.55rem; border-radius: 999px; background: #e7f1ff; color: #0d6efd; font-size: 0.75rem; font-weight: 700; white-space: nowrap; }
  .back-button { display: inline-flex; align-items: center; justify-content: center; width: 34px; height: 34px; padding: 0; border: 1px solid #ced4da; border-radius: 8px; background: #fff; color: #495057; }
  .back-button:hover:not(:disabled) { border-color: #0d6efd; color: #0d6efd; background: #f0f6ff; }
  .back-button:disabled { cursor: not-allowed; opacity: 0.55; }
  .selected-summary { margin: 0 0 0.75rem; color: #555; font-size: 0.9rem; }
  .assignment-field { max-width: 360px; }
  .assignment-field label { display: block; margin-bottom: 0.35rem; color: #555; font-size: 0.82rem; font-weight: 600; }
  .assignment-field small { display: block; margin-top: 0.4rem; color: #664d03; }
  .optional { color: #6c757d; font-weight: 400; }
  .required { color: #dc3545; }
  .maximum-amount-help { display: flex; align-items: baseline; justify-content: space-between; gap: 0.75rem; }
  .use-maximum-button { flex: 0 0 auto; padding: 0; border: 0; background: transparent; color: #0d6efd; font-size: 0.78rem; font-weight: 600; text-decoration: underline; text-underline-offset: 2px; }
  .use-maximum-button:hover:not(:disabled) { color: #0a58ca; }
  .use-maximum-button:disabled { cursor: not-allowed; opacity: 0.55; }
  .assigned-table-wrapper { width: 100%; max-height: unset; overflow: visible; }
  .assigned-table-wrapper table { width: 100%; table-layout: fixed; }
  .assigned-table-wrapper th:nth-child(1) { width: 7%; }
  .assigned-table-wrapper th:nth-child(2) { width: 21%; }
  .assigned-table-wrapper th:nth-child(3) { width: 32%; }
  .assigned-table-wrapper th:nth-child(4) { width: 15%; }
  .assigned-table-wrapper th:nth-child(5) { width: 25%; }
  .empty { flex: 1; display: grid; place-items: center; padding: 1rem; color: #777; border: 1px dashed #ddd; border-radius: 8px; }
  .player-status { display: inline-flex; padding: 0.25rem 0.55rem; border-radius: 999px; font-size: 0.72rem; font-weight: 600; }
  .active-status { background: #d1e7dd; color: #0f5132; }
  .inactive-status { background: #e9ecef; color: #6c757d; }
  .btn-icon { display: inline-flex; align-items: center; justify-content: center; width: 32px; height: 32px; padding: 0; border: 1px solid transparent; border-radius: 8px; }
  .btn-delete { border-color: #f1aeb5; background: #fff5f5; color: #dc3545; }
  .btn-delete:hover:not(:disabled) { border-color: #dc3545; background: #dc3545; color: #fff; }
  .btn-delete:disabled { cursor: not-allowed; opacity: 0.45; }
  .btn-delete-all { display: inline-flex; align-items: center; min-height: 32px; gap: 0.3rem; padding: 0.25rem 0.5rem; border-width: 1px; border-style: solid; border-radius: 8px; font-size: 0.75rem; }
  @media (max-width: 640px) {
    .fixed-modal { margin: 0.5rem; }
    .modal-fixed { height: calc(100vh - 1rem); }
    .top-actions { align-items: flex-start !important; flex-direction: column; }
    .assigned-table-wrapper { overflow-x: auto; }
    .assigned-table-wrapper table { min-width: 680px; }
  }
</style>
